import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';

// Editorial theme colors
const COLORS = {
  cream: '#FAF8F5',
  charcoal: '#2D2A26',
  terracotta: '#C45D3A',
  border: '#2D2A26'
};

const FooterContainer = styled(Box)({
  backgroundColor: COLORS.charcoal,
  color: COLORS.cream,
  padding: '64px',
  '@media (max-width: 768px)': {
    padding: '48px 20px'
  }
});

const FooterContent = styled(Box)({
  maxWidth: '1400px',
  margin: '0 auto',
  display: 'grid',
  gridTemplateColumns: '2fr 1fr 1fr',
  gap: '64px',
  '@media (max-width: 968px)': {
    gridTemplateColumns: '1fr',
    gap: '48px',
    textAlign: 'center'
  }
});

const BrandSection = styled(Box)({
  '@media (max-width: 968px)': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  }
});

const BusinessName = styled(Typography)({
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: '32px',
  fontWeight: 700,
  color: COLORS.cream,
  letterSpacing: '-1px',
  marginBottom: '16px',
  '@media (max-width: 768px)': {
    fontSize: '28px'
  }
});

const Tagline = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  fontWeight: 400,
  color: `${COLORS.cream}99`,
  textTransform: 'uppercase',
  letterSpacing: '2px',
  marginBottom: '24px'
});

const FooterSection = styled(Box)({
  '@media (max-width: 968px)': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  }
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '12px',
  fontWeight: 700,
  color: COLORS.cream,
  textTransform: 'uppercase',
  letterSpacing: '2px',
  marginBottom: '24px'
});

const ContactItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '16px',
  cursor: 'pointer',
  transition: 'color 0.2s ease',
  '&:hover': {
    '& .contact-text': {
      color: COLORS.terracotta
    },
    '& svg': {
      color: COLORS.terracotta
    }
  },
  '@media (max-width: 968px)': {
    justifyContent: 'center'
  }
});

const ContactText = styled(Typography)({
  fontFamily: "'Space Mono', monospace",
  fontSize: '14px',
  fontWeight: 400,
  color: `${COLORS.cream}cc`,
  transition: 'color 0.2s ease'
});

const QuickLink = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  fontWeight: 400,
  color: `${COLORS.cream}cc`,
  marginBottom: '12px',
  cursor: 'pointer',
  transition: 'color 0.2s ease',
  '&:hover': {
    color: COLORS.terracotta
  }
});

const Divider = styled(Box)({
  height: '1px',
  backgroundColor: `${COLORS.cream}20`,
  margin: '48px 0 24px',
  maxWidth: '1400px',
  marginLeft: 'auto',
  marginRight: 'auto'
});

const Copyright = styled(Box)({
  maxWidth: '1400px',
  margin: '0 auto',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    gap: '16px',
    textAlign: 'center'
  }
});

const CopyrightText = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '13px',
  fontWeight: 400,
  color: `${COLORS.cream}66`
});

const PoweredBy = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '13px',
  fontWeight: 400,
  color: `${COLORS.cream}66`,
  '& a': {
    color: COLORS.terracotta,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  }
});

const EditorialFooter = ({ businessInfo, contactInfo }) => {
  const handleNavClick = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handlePhoneClick = () => {
    if (contactInfo?.phone || businessInfo?.phone) {
      window.location.href = `tel:${contactInfo?.phone || businessInfo?.phone}`;
    }
  };

  const handleEmailClick = () => {
    if (contactInfo?.email || businessInfo?.email) {
      window.location.href = `mailto:${contactInfo?.email || businessInfo?.email}`;
    }
  };

  return (
    <FooterContainer id="contact">
      <FooterContent>
        <BrandSection>
          <BusinessName>{businessInfo?.name || 'Business'}</BusinessName>
          <Tagline>{businessInfo?.category || 'Quality Service'}</Tagline>
        </BrandSection>

        <FooterSection>
          <SectionTitle>Quick Links</SectionTitle>
          <QuickLink onClick={() => handleNavClick('#home')}>Home</QuickLink>
          <QuickLink onClick={() => handleNavClick('#services')}>Services</QuickLink>
          <QuickLink onClick={() => handleNavClick('#gallery')}>Gallery</QuickLink>
          <QuickLink onClick={() => handleNavClick('#reviews')}>Reviews</QuickLink>
          <QuickLink onClick={() => handleNavClick('#location')}>Location</QuickLink>
        </FooterSection>

        <FooterSection>
          <SectionTitle>Contact</SectionTitle>
          {(contactInfo?.phone || businessInfo?.phone) && (
            <ContactItem onClick={handlePhoneClick}>
              <PhoneIcon sx={{ fontSize: 18, color: `${COLORS.cream}99` }} />
              <ContactText className="contact-text">
                {contactInfo?.phone || businessInfo?.phone}
              </ContactText>
            </ContactItem>
          )}
          {(contactInfo?.email || businessInfo?.email) && (
            <ContactItem onClick={handleEmailClick}>
              <EmailIcon sx={{ fontSize: 18, color: `${COLORS.cream}99` }} />
              <ContactText className="contact-text">
                {contactInfo?.email || businessInfo?.email}
              </ContactText>
            </ContactItem>
          )}
        </FooterSection>
      </FooterContent>

      <Divider />

      <Copyright>
        <CopyrightText>
          © {new Date().getFullYear()} {businessInfo?.name || 'Business'}. All rights reserved.
        </CopyrightText>
        <PoweredBy>
          Powered by <a href="https://visibeen.com" target="_blank" rel="noopener noreferrer">Visibeen</a>
        </PoweredBy>
      </Copyright>
    </FooterContainer>
  );
};

export default EditorialFooter;
